import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Paper,
  Chip,
  IconButton,
  MenuItem,
  Grid,
  Divider,
  Avatar,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Checkbox
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import CreateIcon from '@mui/icons-material/Create';
import PersonIcon from '@mui/icons-material/Person';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import DescriptionIcon from '@mui/icons-material/Description';
import GavelIcon from '@mui/icons-material/Gavel';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ArticleIcon from '@mui/icons-material/Article';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ReactMarkdown from 'react-markdown';
import { apiService } from '../api';
import { auth } from '../firebase';

function AIParalegal() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [linkedContext, setLinkedContext] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Document drafting state
  const [documentType, setDocumentType] = useState('');
  const [draftDialogOpen, setDraftDialogOpen] = useState(false);
  const [selectedDraftCase, setSelectedDraftCase] = useState('');
  const [caseDocuments, setCaseDocuments] = useState([]);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [additionalInstructions, setAdditionalInstructions] = useState('');
  const [templateFile, setTemplateFile] = useState(null);
  const [uploadingTemplate, setUploadingTemplate] = useState(false);

  const documentTypes = [
    { value: 'affidavit', label: 'Affidavit', description: 'Sworn statement with strict legal structure' },
    { value: 'will', label: 'Will and Testament', description: 'Legal document for property disposition' },
    { value: 'legal_notice', label: 'Legal Notice', description: 'Formal notice under statutory provisions' },
    { value: 'plaint', label: 'Plaint (Civil Suit)', description: 'Statement of claim in civil proceedings' },
    { value: 'written_statement', label: 'Written Statement (Defense)', description: 'Defense statement in civil suit' },
    { value: 'vakalatnama', label: 'Vakalatnama', description: 'Power of attorney for legal representation' },
    { value: 'custom', label: 'Custom Document', description: 'Upload your own template for custom document format' }
  ];

  useEffect(() => {
    fetchCases();
  }, []);

  useEffect(() => {
    if (selectedDraftCase) {
      fetchCaseDocuments();
    } else {
      setCaseDocuments([]);
      setSelectedDocuments([]);
    }
  }, [selectedDraftCase]);

  const fetchCases = async () => {
    try {
      setLoading(true);
      const response = await apiService.getCases();
      setCases(response.data.cases || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching cases:', err);
      setError('Failed to load cases');
    } finally {
      setLoading(false);
    }
  };

  const fetchCaseDocuments = async () => {
    try {
      const response = await apiService.getCaseDocuments(selectedDraftCase);
      setCaseDocuments(response.data.documents || []);
    } catch (err) {
      console.error('Error fetching documents:', err);
      setError('Failed to load case documents');
    }
  };

  const handleOpenDraftDialog = () => {
    if (cases.length === 0) {
      setError('Please create a case first before drafting documents');
      return;
    }
    setDraftDialogOpen(true);
  };

  const handleCloseDraftDialog = () => {
    setDraftDialogOpen(false);
    setDocumentType('');
    setSelectedDraftCase('');
    setSelectedDocuments([]);
    setAdditionalInstructions('');
    setTemplateFile(null);
  };

  const handleTemplateUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setTemplateFile(file);
    }
  };

  const handleRemoveTemplate = () => {
    setTemplateFile(null);
  };

  const handleToggleDocument = (docId) => {
    if (selectedDocuments.includes(docId)) {
      setSelectedDocuments(selectedDocuments.filter(id => id !== docId));
    } else {
      setSelectedDocuments([...selectedDocuments, docId]);
    }
  };

  const handleDraftDocument = async () => {
    if (!documentType) {
      setError('Please select a document type');
      return;
    }
    if (!selectedDraftCase) {
      setError('Please select a case');
      return;
    }
    if (documentType === 'custom' && !templateFile) {
      setError('Please upload a template for custom document type');
      return;
    }

    setIsTyping(true);
    handleCloseDraftDialog();

    try {
      // Create a specialized prompt for document drafting
      const docTypeLabel = documentTypes.find(d => d.value === documentType)?.label || documentType;
      const selectedCase = cases.find(c => c.id === selectedDraftCase);
      
      let draftPrompt = '';
      
      // If template is provided, use template-based prompt
      if (templateFile) {
        draftPrompt = `Create a ${docTypeLabel} document using the provided template format.\n\n`;
        draftPrompt += `CRITICAL INSTRUCTIONS:\n`;
        draftPrompt += `- Follow the EXACT structure and format of the template provided\n`;
        draftPrompt += `- Replace placeholder text with actual information from the case\n`;
        draftPrompt += `- Maintain all sections, headings, and formatting from the template\n`;
        draftPrompt += `- Do NOT include any explanatory text, introductions, or summaries\n`;
        draftPrompt += `- Return ONLY the completed document following the template format\n\n`;
      } else {
        draftPrompt = `Create a complete ${docTypeLabel} document following Indian legal standards.\n\n`;
        draftPrompt += `CRITICAL INSTRUCTIONS:\n`;
        draftPrompt += `- Return ONLY the document content itself\n`;
        draftPrompt += `- Do NOT include any introductory text like "Here is the draft" or "Below is the document"\n`;
        draftPrompt += `- Do NOT include any explanations, summaries, or notes about the document\n`;
        draftPrompt += `- Do NOT include disclaimers or caveats\n`;
        draftPrompt += `- Start directly with the document heading/title\n\n`;
      }
      
      draftPrompt += `Case Details:\n`;
      draftPrompt += `Title: ${selectedCase?.title || 'N/A'}\n`;
      draftPrompt += `Description: ${selectedCase?.description || 'N/A'}\n\n`;
      
      if (selectedDocuments.length > 0) {
        draftPrompt += `Context Documents: ${selectedDocuments.length} case document(s) selected for reference\n`;
        draftPrompt += `Document IDs: ${selectedDocuments.join(', ')}\n\n`;
      }
      
      if (additionalInstructions) {
        draftPrompt += `Specific Requirements:\n${additionalInstructions}\n\n`;
      }
      
      if (!templateFile) {
        draftPrompt += `Mandatory Components:\n`;
        
        // Add specific requirements based on document type
        switch (documentType) {
          case 'affidavit':
            draftPrompt += `- Title/Heading as "Affidavit"\n- Deponent's details (name, father's name, age, address, occupation)\n- Oath/Solemn Affirmation\n- Numbered paragraphs with facts\n- Verification clause\n- Attestation section for Notary/Oath Commissioner`;
            break;
          case 'will':
            draftPrompt += `- Declaration as "Last Will and Testament"\n- Revocation clause for previous wills\n- Testator's details\n- Executor appointment\n- Specific bequests/legacies\n- Residuary clause\n- Signature section with witness attestation`;
            break;
          case 'legal_notice':
            draftPrompt += `- Advocate's details\n- Client's details\n- Recipient's details\n- Detailed chronological facts\n- Clear demand/relief sought\n- Consequence of non-compliance\n- Time frame for response (15-30 days)`;
            break;
          case 'plaint':
            draftPrompt += `- Title/Cause Title with Court name\n- Jurisdiction statement\n- Facts in numbered paragraphs\n- Relief claimed\n- Valuation and court fee statement\n- Verification clause`;
            break;
          case 'written_statement':
            draftPrompt += `- Title/Cause Title\n- Jurisdiction statement\n- Defense facts in numbered paragraphs\n- Denial/admission of plaintiff's claims\n- Counter-claims if any\n- Verification clause`;
            break;
          case 'vakalatnama':
            draftPrompt += `- Case details (Court name, case number)\n- Names of all parties\n- Client's authorization clause\n- Advocate's acceptance\n- Client's signature section`;
            break;
        }
      }

      const userMessage = {
        id: Date.now(),
        type: 'user',
        text: `Draft ${docTypeLabel}${templateFile ? ' (with template)' : ''}${selectedDocuments.length > 0 ? ` (using ${selectedDocuments.length} document${selectedDocuments.length > 1 ? 's' : ''})` : ''}`,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, userMessage]);

      let response;
      
      // If template file is provided, we need to send it differently
      if (templateFile) {
        // Use FormData to send template file
        const formData = new FormData();
        formData.append('template', templateFile);
        formData.append('case_id', selectedDraftCase);
        formData.append('message', draftPrompt);
        if (selectedDocuments.length > 0) {
          formData.append('document_ids', JSON.stringify(selectedDocuments));
        }
        
        const token = await auth.currentUser.getIdToken();
        const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:5002';
        
        const fetchResponse = await fetch(`${backendUrl}/api/ai/chat-with-template`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });
        
        const data = await fetchResponse.json();
        response = { data };
      } else {
        response = await apiService.chatWithAI(
          selectedDraftCase, 
          draftPrompt, 
          selectedDocuments.length > 0 ? selectedDocuments : null
        );
      }
      
      const aiResponse = {
        id: Date.now() + 1,
        type: 'ai',
        text: response.data.response,
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, aiResponse]);
    } catch (err) {
      console.error('Error drafting document:', err);
      const errorResponse = {
        id: Date.now() + 1,
        type: 'ai',
        text: 'I apologize, but I encountered an error drafting the document. Please try again.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorResponse]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleSend = async () => {
    if (!input.trim()) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      text: input,
      timestamp: new Date()
    };

    setMessages([...messages, userMessage]);
    const currentInput = input;
    setInput('');
    setIsTyping(true);

    try {
      // Call real AI API
      const response = await apiService.chatWithAI(linkedContext || null, currentInput);
      
      const aiResponse = {
        id: Date.now() + 1,
        type: 'ai',
        text: response.data.response,
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, aiResponse]);
    } catch (err) {
      console.error('Error calling AI:', err);
      const errorResponse = {
        id: Date.now() + 1,
        type: 'ai',
        text: 'I apologize, but I encountered an error processing your request. Please try again.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorResponse]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleCopyMessage = (text) => {
    navigator.clipboard.writeText(text);
  };

  const extractDocumentContent = (text) => {
    // Remove common AI introductory phrases and summaries
    let cleanedText = text;
    
    // Remove lines that are introductory/explanatory (not part of the actual document)
    const linesToRemove = [
      /^(Here('s| is)|I('ve| have)|This is|Below is|I'm providing|Please find)/i,
      /^(The following is|I will draft|Let me draft|I'll draft)/i,
      /^(Summary|Note|Important|Please note|Disclaimer)/i,
      /^(This document|The document|This draft)/i
    ];
    
    // Split into lines
    const lines = cleanedText.split('\n');
    let documentStarted = false;
    let cleanedLines = [];
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      
      // Skip empty lines at the beginning
      if (!documentStarted && !line) continue;
      
      // Check if this line is an introduction/explanation
      const isIntroLine = linesToRemove.some(pattern => pattern.test(line));
      
      // If we haven't started the document yet and this is an intro line, skip it
      if (!documentStarted && isIntroLine) continue;
      
      // Check for common document headers that indicate the actual document has started
      const isDocumentHeader = /^(AFFIDAVIT|IN THE |WILL AND TESTAMENT|LEGAL NOTICE|PLAINT|WRITTEN STATEMENT|VAKALATNAMA|TO:|FROM:)/i.test(line);
      
      if (isDocumentHeader) {
        documentStarted = true;
      }
      
      // Once we've found the document start, include everything
      if (documentStarted || (!isIntroLine && line.length > 0)) {
        documentStarted = true;
        cleanedLines.push(lines[i]); // Keep original formatting
      }
    }
    
    // Join back and trim
    return cleanedLines.join('\n').trim();
  };

  const handleExportPDF = async (text, messageId) => {
    try {
      // Extract only the document content, removing AI intro/summary
      const documentContent = extractDocumentContent(text);
      const response = await apiService.exportDocumentPDF(documentContent);
      
      // Create blob from response
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `document_${messageId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting PDF:', err);
      setError('Failed to export as PDF');
    }
  };

  const handleExportWord = async (text, messageId) => {
    try {
      // Extract only the document content, removing AI intro/summary
      const documentContent = extractDocumentContent(text);
      const response = await apiService.exportDocumentWord(documentContent);
      
      // Create blob from response
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `document_${messageId}.docx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting Word:', err);
      setError('Failed to export as Word document');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 1, fontWeight: 700 }}>
        Paralegal
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Your intelligent legal assistant for drafting, research, and analysis
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Prompt Starters & Context */}
        <Grid item xs={12} md={3}>
          <Card sx={{ mb: 2, boxShadow: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Document Drafting
              </Typography>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<GavelIcon />}
                onClick={handleOpenDraftDialog}
                sx={{ mb: 1 }}
              >
                Draft Legal Document
              </Button>
              <Typography variant="caption" color="text.secondary">
                AI-powered drafting of affidavits, wills, notices, pleadings, and more
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ boxShadow: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Context Linking
              </Typography>
              <TextField
                fullWidth
                select
                size="small"
                label="Link to Case"
                value={linkedContext}
                onChange={(e) => setLinkedContext(e.target.value)}
              >
                <MenuItem value="">None</MenuItem>
                {cases.length === 0 ? (
                  <MenuItem disabled>No cases available</MenuItem>
                ) : (
                  cases.map((caseItem) => (
                    <MenuItem key={caseItem.id} value={caseItem.id}>
                      {caseItem.title}
                    </MenuItem>
                  ))
                )}
              </TextField>
              {linkedContext && (
                <Chip
                  label={`Linked: ${cases.find(c => c.id === linkedContext)?.title || 'Case'}`}
                  onDelete={() => setLinkedContext('')}
                  size="small"
                  color="primary"
                  sx={{ mt: 2 }}
                />
              )}
              {cases.length === 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                  No cases available. Create a case to link context.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Chat Interface */}
        <Grid item xs={12} md={9}>
          <Card sx={{ boxShadow: 2, height: 'calc(100vh - 250px)', display: 'flex', flexDirection: 'column' }}>
            {/* Chat Messages */}
            <Box sx={{ flex: 1, overflowY: 'auto', p: 3, bgcolor: 'grey.50' }}>
              {messages.length === 0 && !isTyping && (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <CreateIcon sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    Hello! I'm your Paralegal assistant.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    I can help you draft documents, research legal questions, and provide analysis.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Click a quick prompt or type your question below to get started.
                  </Typography>
                </Box>
              )}
              {messages.map((message) => (
                <Box
                  key={message.id}
                  sx={{
                    display: 'flex',
                    justifyContent: message.type === 'user' ? 'flex-end' : 'flex-start',
                    mb: 3
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 2, maxWidth: '85%', alignItems: 'flex-start' }}>
                    {message.type === 'ai' && (
                      <Avatar sx={{ bgcolor: 'primary.main', mt: 0.5 }}>
                        <CreateIcon />
                      </Avatar>
                    )}
                    <Paper
                      sx={{
                        p: 2,
                        bgcolor: message.type === 'user' ? 'primary.main' : 'white',
                        color: message.type === 'user' ? 'white' : 'text.primary',
                        borderRadius: 2,
                        boxShadow: 1
                      }}
                    >
                      <Box sx={{ 
                        '& h2': { fontSize: '1.1rem', fontWeight: 600, mt: 1, mb: 1 },
                        '& h3': { fontSize: '1rem', fontWeight: 600, mt: 1, mb: 0.5 },
                        '& ul, & ol': { pl: 3, my: 1 },
                        '& li': { mb: 0.5 },
                        '& p': { mb: 1 }
                      }}>
                        <ReactMarkdown>{message.text}</ReactMarkdown>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, mt: 1, justifyContent: 'flex-end' }}>
                        {message.type === 'ai' && (
                          <>
                            <IconButton 
                              size="small" 
                              sx={{ color: 'inherit' }}
                              onClick={() => handleCopyMessage(message.text)}
                              title="Copy to clipboard"
                            >
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                            <IconButton 
                              size="small" 
                              sx={{ color: 'inherit' }}
                              onClick={() => handleExportPDF(message.text, message.id)}
                              title="Export as PDF"
                            >
                              <PictureAsPdfIcon fontSize="small" />
                            </IconButton>
                            <IconButton 
                              size="small" 
                              sx={{ color: 'inherit' }}
                              onClick={() => handleExportWord(message.text, message.id)}
                              title="Export as Word"
                            >
                              <ArticleIcon fontSize="small" />
                            </IconButton>
                          </>
                        )}
                      </Box>
                    </Paper>
                    {message.type === 'user' && (
                      <Avatar sx={{ bgcolor: 'secondary.main', mt: 0.5 }}>
                        <PersonIcon />
                      </Avatar>
                    )}
                  </Box>
                </Box>
              ))}

              {isTyping && (
                <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                  <Avatar sx={{ bgcolor: 'primary.main' }}>
                    <CreateIcon />
                  </Avatar>
                  <Paper sx={{ p: 2, bgcolor: 'white', borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      AI is typing...
                    </Typography>
                  </Paper>
                </Box>
              )}
            </Box>

            <Divider />

            {/* Input Area */}
            <Box sx={{ p: 2, bgcolor: 'white' }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  fullWidth
                  multiline
                  maxRows={4}
                  placeholder="Ask your Paralegal anything..."
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                  size="small"
                />
                <Button
                  variant="contained"
                  onClick={handleSend}
                  disabled={!input.trim() || isTyping}
                  endIcon={<SendIcon />}
                >
                  Send
                </Button>
              </Box>
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Document Drafting Dialog */}
      <Dialog open={draftDialogOpen} onClose={handleCloseDraftDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <GavelIcon color="primary" />
            Draft Legal Document
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Select a document type and case. Optionally select case documents to provide context for AI drafting.
          </Typography>

          <TextField
            fullWidth
            select
            label="Document Type"
            value={documentType}
            onChange={(e) => setDocumentType(e.target.value)}
            margin="normal"
            required
            SelectProps={{
              MenuProps: {
                PaperProps: {
                  style: {
                    maxHeight: 400,
                  },
                },
              },
            }}
          >
            <MenuItem value="">Select a document type</MenuItem>
            {documentTypes.map((type) => (
              <MenuItem key={type.value} value={type.value}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {type.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {type.description}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </TextField>

          <TextField
            fullWidth
            select
            label="Select Case"
            value={selectedDraftCase}
            onChange={(e) => setSelectedDraftCase(e.target.value)}
            margin="normal"
            required
            helperText="Choose the case for which this document is being drafted"
          >
            <MenuItem value="">Select a case</MenuItem>
            {cases.map((caseItem) => (
              <MenuItem key={caseItem.id} value={caseItem.id}>
                {caseItem.title}
              </MenuItem>
            ))}
          </TextField>

          {selectedDraftCase && caseDocuments.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Select Case Documents (Optional)
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                Select one or more documents to provide context for AI drafting
              </Typography>
              <Paper variant="outlined" sx={{ maxHeight: 200, overflow: 'auto' }}>
                <List>
                  {caseDocuments.map((doc) => (
                    <ListItem
                      key={doc.id}
                      button
                      onClick={() => handleToggleDocument(doc.id)}
                      sx={{
                        bgcolor: selectedDocuments.includes(doc.id) ? 'primary.light' : 'transparent'
                      }}
                    >
                      <Checkbox
                        checked={selectedDocuments.includes(doc.id)}
                        tabIndex={-1}
                        disableRipple
                      />
                      <DescriptionIcon sx={{ mr: 1, color: 'primary.main' }} />
                      <ListItemText
                        primary={doc.filename}
                        secondary={new Date(doc.uploaded_at?.seconds * 1000 || doc.uploaded_at).toLocaleDateString()}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
              {selectedDocuments.length > 0 && (
                <Typography variant="caption" color="primary" sx={{ mt: 1, display: 'block' }}>
                  {selectedDocuments.length} document(s) selected for context
                </Typography>
              )}
            </Box>
          )}

          <TextField
            fullWidth
            multiline
            rows={3}
            label="Additional Instructions (Optional)"
            value={additionalInstructions}
            onChange={(e) => setAdditionalInstructions(e.target.value)}
            margin="normal"
            placeholder="E.g., Include specific clauses, mention particular facts, use formal/informal tone..."
            helperText="Provide any specific requirements or details for the document"
          />

          <Box sx={{ 
            mt: 3, 
            p: 2, 
            border: documentType === 'custom' ? 2 : 1,
            borderColor: documentType === 'custom' ? 'primary.main' : 'divider',
            borderRadius: 2,
            bgcolor: documentType === 'custom' ? 'primary.50' : 'transparent'
          }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: documentType === 'custom' ? 'primary.main' : 'inherit' }}>
              Upload Template {documentType === 'custom' ? '(Required)' : '(Optional)'}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
              {documentType === 'custom' 
                ? 'Template is required for custom documents. Upload your document template that Gemini will use as the format.'
                : 'Upload a document template that Gemini will use as the format for creating the document'}
            </Typography>
            
            {!templateFile ? (
              <Box>
                <input
                  accept=".pdf,.doc,.docx,.txt"
                  style={{ display: 'none' }}
                  id="template-upload"
                  type="file"
                  onChange={handleTemplateUpload}
                />
                <label htmlFor="template-upload">
                  <Button
                    variant={documentType === 'custom' ? 'contained' : 'outlined'}
                    component="span"
                    startIcon={<CloudUploadIcon />}
                    fullWidth
                  >
                    Upload Template
                  </Button>
                </label>
              </Box>
            ) : (
              <Paper variant="outlined" sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DescriptionIcon color="primary" />
                  <Box>
                    <Typography variant="body2">{templateFile.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {(templateFile.size / 1024).toFixed(2)} KB
                    </Typography>
                  </Box>
                </Box>
                <IconButton size="small" onClick={handleRemoveTemplate} color="error">
                  <DeleteIcon />
                </IconButton>
              </Paper>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDraftDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleDraftDocument}
            disabled={!documentType || !selectedDraftCase || (documentType === 'custom' && !templateFile)}
            startIcon={<GavelIcon />}
          >
            Draft Document
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AIParalegal;